class SecureStorageService {
  private readonly SECRET_KEY = import.meta.env.VITE_STORAGE_SECRET_KEY;
  private readonly SECURE_KEYS = ['user', 'avatar'];
  // eslint-disable-next-line
  private cache: { [key: string]: any } = {};

  // eslint-disable-next-line
  getItem(key: string): any {
    if (this.SECURE_KEYS.includes(key)) {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) return null;

      // Si tenemos el valor en caché, lo devolvemos
      if (this.cache[key]) return this.cache[key];

      try {
        // Intentar parse directo primero
        const value = JSON.parse(encryptedValue);
        this.cache[key] = value;
        return value;
      } catch {
        // Si falla, devolvemos null y empezamos desencriptación asíncrona
        this.decryptInBackground(key, encryptedValue);
        return null;
      }
    }

    // Para claves no seguras, comportamiento normal
    const value = localStorage.getItem(key);
    try {
      return value ? JSON.parse(value) : null;
    } catch {
      return value;
    }
  }

  // eslint-disable-next-line
  setItem(key: string, value: any): void {
    if (this.SECURE_KEYS.includes(key)) {
      // Guardar inmediatamente en caché y localStorage sin encriptar
      this.cache[key] = value;
      localStorage.setItem(key, JSON.stringify(value));

      // Encriptar en background
      this.encryptInBackground(key, value);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  removeItem(key: string): void {
    delete this.cache[key];
    localStorage.removeItem(key);
  }

  private async decryptInBackground(
    key: string,
    encryptedValue: string
  ): Promise<void> {
    try {
      const encryptedBytes = Uint8Array.from(atob(encryptedValue), c =>
        c.charCodeAt(0)
      );
      const iv = encryptedBytes.slice(0, 12);
      const data = encryptedBytes.slice(12);

      const keyBytes = new TextEncoder().encode(this.SECRET_KEY);
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-GCM' },
        false,
        ['decrypt']
      );

      const decryptedData = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        cryptoKey,
        data
      );

      const decryptedString = new TextDecoder().decode(decryptedData);
      const value = JSON.parse(decryptedString);
      this.cache[key] = value;
    } catch (error) {
      console.error('Error during decryption:', error);
    }
  }

  // eslint-disable-next-line
  private async encryptInBackground(key: string, value: any): Promise<void> {
    try {
      const dataString = JSON.stringify(value);
      const textBytes = new TextEncoder().encode(dataString);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const keyBytes = new TextEncoder().encode(this.SECRET_KEY);

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'AES-GCM' },
        false,
        ['encrypt']
      );

      const encryptedData = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        cryptoKey,
        textBytes
      );

      const encryptedArray = new Uint8Array(
        iv.length + encryptedData.byteLength
      );
      encryptedArray.set(iv);
      encryptedArray.set(new Uint8Array(encryptedData), iv.length);

      const encryptedString = btoa(
        String.fromCharCode.apply(null, Array.from(encryptedArray))
      );
      localStorage.setItem(key, encryptedString);
    } catch (error) {
      console.error('Error during encryption:', error);
    }
  }
}

export const secureStorage = new SecureStorageService();
